/* global Office Uint8Array */

/** 分割の粒度。Office.js の既定より大きくしていない（実測で決める・記録 35）。 */
const SLICE_SIZE = 65536;

/**
 * 同時に投げるスライス要求の本数。
 *
 * **全スライスを一度に投げてはいけない。** 実物のフォーマットは 18〜23MB あり、64KB 区切りだと
 * 272〜356 のスライスになる。以前は 1 つのループで全部の `getSliceAsync` を発行していたので、
 * WebView2 と Office の橋を通る要求が数百本同時に走っていた（記録 35）。上限いっぱいの 100MB
 * なら 1,600 本になる。順に取れば要求は 1 本で済むが往復の数だけ待つので、束にして折り合いを
 * 付ける。適切な本数は実測で決める話で、ここは控えめな既定である。
 */
const SLICE_CONCURRENCY = 4;

/**
 * 現在 PowerPoint で開いている文書を **PPTX バイト列**として取得する。
 *
 * `Office.context.document.getFileAsync(Compressed)` でスライス分割取得し連結する。
 * スライドフォーマット登録（現在の PPTX をアップロード）で用いる。
 */
export async function getCurrentFileBytes(): Promise<Uint8Array> {
    const file = await openCompressedFile();
    try {
        return await readAllSlices(file);
    } finally {
        // 閉じ忘れると Office 側がファイルを掴んだままになる。成否に依らず必ず閉じる。
        file.closeAsync(() => undefined);
    }
}

/** 圧縮形式（pptx）として開く。 */
function openCompressedFile(): Promise<Office.File> {
    return new Promise((resolve, reject) => {
        Office.context.document.getFileAsync(
            Office.FileType.Compressed,
            { sliceSize: SLICE_SIZE },
            (result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    reject(new Error(result.error?.message ?? "getFileAsync に失敗しました"));
                    return;
                }
                resolve(result.value);
            }
        );
    });
}

/** スライスを束ごとに取り、届いた順ではなく**索引の順**に連結する。 */
async function readAllSlices(file: Office.File): Promise<Uint8Array> {
    const sliceCount = file.sliceCount;
    if (sliceCount === 0) return new Uint8Array(0);

    const slices: Uint8Array[] = new Array(sliceCount);
    for (let start = 0; start < sliceCount; start += SLICE_CONCURRENCY) {
        const end = Math.min(start + SLICE_CONCURRENCY, sliceCount);
        const batch: Promise<void>[] = [];
        for (let i = start; i < end; i++) {
            batch.push(
                getSlice(file, i).then((slice) => {
                    slices[slice.index] = new Uint8Array(slice.data as ArrayLike<number>);
                })
            );
        }
        // 束の中の 1 本が失敗したら、そこで投げる（残りの束は投げない）。
        await Promise.all(batch);
    }

    const total = slices.reduce((n, s) => n + s.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const s of slices) {
        out.set(s, offset);
        offset += s.length;
    }
    return out;
}

function getSlice(file: Office.File, index: number): Promise<Office.Slice> {
    return new Promise((resolve, reject) => {
        file.getSliceAsync(index, (result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
                reject(new Error(result.error?.message ?? "getSliceAsync に失敗しました"));
                return;
            }
            resolve(result.value);
        });
    });
}

/** 現在の文書名（拡張子なし）を best-effort で返す。取得できなければ既定名。 */
export function getDocumentName(): string {
    try {
        const url = Office.context.document.url;
        if (url) {
            const base = url.split(/[\\/]/).pop() ?? "";
            const name = base.replace(/\.pptx$/i, "").trim();
            if (name) return name;
        }
    } catch {
        // 取得不可。既定名へ。
    }
    return "プレゼンテーション";
}
